/**
 * /messages — Direct messaging & announcements hub.
 *
 * Roles:
 *  - strata_admin / property_manager: can message All Hosts, All Owners,
 *    All Guests (email only), Specific Person, Everyone
 *  - host: can message Property Manager / Council only
 *
 * Features:
 *  - Compose sheet (subject, body, recipient selector, delivery options)
 *  - Inbox tab (messages received, with read tracking)
 *  - Sent tab (messages you sent)
 *  - Message detail view (expand full body)
 */
import { useState } from "react";
import {
  MessageSquare, Send, Inbox, PenSquare, Users, User,
  Home, Building2, UserCog, ChevronDown, ChevronUp,
  Mail, Bell, Loader2, CheckCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useMyAssignments } from "@/hooks/useUserAssignments";
import {
  useSentMessages, useInboxMessages, useMarkMessageRead,
  useSendMessage, useUnreadMessageCount,
  type SendMessagePayload,
} from "@/hooks/useMessages";
import type { Message, MessageRecipientScope } from "@/types/database";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

const scopeLabels: Record<MessageRecipientScope, string> = {
  all_hosts:     "All Hosts",
  all_owners:    "All Unit Owners (email only)",
  all_guests:    "All Active Guests (email only)",
  specific_user: "Specific Person",
  all:           "Everyone in the Building",
};

const scopeIcons: Record<MessageRecipientScope, React.ElementType> = {
  all_hosts:     Users,
  all_owners:    Home,
  all_guests:    User,
  specific_user: User,
  all:           Building2,
};

// ─── Message Row ─────────────────────────────────────────────────────────────

function MessageRow({
  message,
  isRead,
  onRead,
  isInbox,
}: {
  message: Message;
  isRead: boolean;
  onRead: (id: string) => void;
  isInbox: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const ScopeIcon = scopeIcons[message.recipient_scope] ?? Users;

  const handleToggle = () => {
    setExpanded((prev) => !prev);
    if (!isRead && isInbox) onRead(message.id);
  };

  const senderName = message.sender?.full_name ?? "Unknown";
  const senderRole = (message.sender?.role ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div
      className={`border-b last:border-b-0 transition-colors ${
        !isRead && isInbox ? "bg-primary/[0.02] border-l-2 border-l-primary" : "border-l-2 border-l-transparent"
      }`}
    >
      {/* Header row */}
      <button
        type="button"
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted/40 transition-colors"
        onClick={handleToggle}
      >
        {/* Avatar */}
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0 mt-0.5">
          {senderName.split(" ").map((n) => n[0]).join("").slice(0, 2)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold truncate">{message.subject}</span>
            {!isRead && isInbox && (
              <Badge className="h-4 px-1.5 text-[9px] bg-primary text-primary-foreground shrink-0">
                New
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
            <span className="font-medium">{senderName}</span>
            <span>·</span>
            <span className="capitalize">{senderRole}</span>
            {!isInbox && (
              <>
                <span>·</span>
                <ScopeIcon className="h-3 w-3" />
                <span>{scopeLabels[message.recipient_scope]}</span>
              </>
            )}
          </div>
          {!expanded && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-1 leading-relaxed">
              {message.body}
            </p>
          )}
        </div>

        {/* Right: time + expand */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-[10px] text-muted-foreground">
            {formatRelativeTime(message.created_at)}
          </span>
          <div className="flex items-center gap-1.5 text-muted-foreground/60">
            {message.sent_as_email && <Mail className="h-3 w-3" />}
            {message.sent_as_notification && <Bell className="h-3 w-3" />}
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </div>
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4 pl-16">
          <div className="bg-muted/40 rounded-lg p-4 text-sm text-foreground leading-relaxed whitespace-pre-wrap">
            {message.body}
          </div>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <ScopeIcon className="h-3 w-3" />
              Sent to: {scopeLabels[message.recipient_scope]}
            </span>
            {message.sent_as_email && (
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3" /> Also emailed
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Compose Sheet ────────────────────────────────────────────────────────────

function ComposeSheet({
  open,
  onClose,
  orgId,
  userRole,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  userRole: string;
}) {
  const sendMessage = useSendMessage();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [scope, setScope] = useState<MessageRecipientScope>("all_hosts");
  const [sendEmail, setSendEmail] = useState(false);

  const isAdmin = userRole === "strata_admin" || userRole === "property_manager";

  const scopeOptions: { value: MessageRecipientScope; label: string; icon: React.ElementType }[] = isAdmin
    ? [
        { value: "all_hosts",     label: "All Hosts",                 icon: Users      },
        { value: "all_owners",    label: "All Unit Owners (email)",   icon: Home       },
        { value: "all_guests",    label: "Active Guests (email)",     icon: User       },
        { value: "all",           label: "Everyone (all platform users)", icon: Building2 },
      ]
    : [
        { value: "all",           label: "Property Manager / Council", icon: UserCog   },
      ];

  const handleSend = () => {
    if (!subject.trim() || !body.trim()) return;
    sendMessage.mutate(
      { orgId, subject: subject.trim(), body: body.trim(), recipientScope: scope, sendAsEmail: sendEmail },
      {
        onSuccess: () => {
          setSubject("");
          setBody("");
          setScope(isAdmin ? "all_hosts" : "all");
          setSendEmail(false);
          onClose();
        },
      }
    );
  };

  const subjectError = subject.length > 200;
  const bodyError = body.length > 5000;
  const canSend = subject.trim().length > 0 && body.trim().length > 0 && !subjectError && !bodyError;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <PenSquare className="h-4 w-4" />
            Compose Message
          </SheetTitle>
          <SheetDescription className="text-xs">
            Send a message + in-app notification to your building residents.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 pt-2">
          {/* To */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">To *</Label>
            <Select value={scope} onValueChange={(v) => setScope(v as MessageRecipientScope)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {scopeOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        {opt.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {(scope === "all_owners" || scope === "all_guests") && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <Mail className="h-3 w-3" />
                This group has no platform accounts — message will be sent by email only.
              </p>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Subject *</Label>
            <Input
              placeholder="e.g. Parking restriction this weekend"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={subjectError ? "border-destructive" : ""}
            />
            <div className="flex justify-between">
              {subjectError && (
                <p className="text-[11px] text-destructive">Max 200 characters</p>
              )}
              <span className="text-[10px] text-muted-foreground ml-auto">{subject.length}/200</span>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Message *</Label>
            <Textarea
              placeholder="Write your message here…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className={`min-h-[160px] resize-none ${bodyError ? "border-destructive" : ""}`}
            />
            <div className="flex justify-between">
              {bodyError && (
                <p className="text-[11px] text-destructive">Max 5,000 characters</p>
              )}
              <span className="text-[10px] text-muted-foreground ml-auto">{body.length}/5,000</span>
            </div>
          </div>

          {/* Delivery options */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Delivery</Label>
            <div className="rounded-lg border p-3 space-y-2.5">
              <div className="flex items-center gap-2.5">
                <Bell className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm flex-1">In-app notification</span>
                <Badge variant="secondary" className="text-[10px]">Always on</Badge>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm flex-1">Also send by email</span>
                <Checkbox
                  checked={sendEmail}
                  onCheckedChange={(v) => setSendEmail(!!v)}
                  id="send-email-check"
                />
              </div>
            </div>
          </div>

          {/* Send button */}
          <Button
            className="w-full gap-2"
            onClick={handleSend}
            disabled={!canSend || sendMessage.isPending}
          >
            {sendMessage.isPending ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
            ) : (
              <><Send className="h-4 w-4" /> Send Message</>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center">
      <MessageSquare className="h-10 w-10 text-muted-foreground/30 mb-3" />
      <p className="text-sm font-medium text-muted-foreground">No {label} yet</p>
      <p className="text-xs text-muted-foreground mt-1">
        {label === "sent messages"
          ? "Messages you send will appear here."
          : "Messages addressed to you will appear here."}
      </p>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Messages() {
  const { user } = useAuth();
  const { data: assignments = [] } = useMyAssignments();
  const orgId = assignments[0]?.org_id ?? null;

  const { data: inbox = [], isLoading: inboxLoading } = useInboxMessages(orgId);
  const { data: sent = [], isLoading: sentLoading } = useSentMessages(orgId);
  const { data: unreadCount = 0 } = useUnreadMessageCount(orgId);
  const markRead = useMarkMessageRead();

  const [composeOpen, setComposeOpen] = useState(false);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());

  const handleRead = (id: string) => {
    setReadIds((prev) => new Set([...prev, id]));
    markRead.mutate(id);
  };

  if (!orgId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Building2 className="h-10 w-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground">
          You're not assigned to any organization yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Messages</h1>
          <p className="text-sm text-muted-foreground">
            {unreadCount > 0
              ? `${unreadCount} unread message${unreadCount !== 1 ? "s" : ""}`
              : "Direct communication with your building community"}
          </p>
        </div>
        <Button onClick={() => setComposeOpen(true)} className="gap-2">
          <PenSquare className="h-4 w-4" />
          Compose
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="inbox">
        <TabsList className="h-8">
          <TabsTrigger value="inbox" className="text-xs gap-1.5">
            <Inbox className="h-3.5 w-3.5" />
            Inbox
            {unreadCount > 0 && (
              <Badge variant="destructive" className="h-4 min-w-[18px] px-1 text-[9px] ml-0.5">
                {unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sent" className="text-xs gap-1.5">
            <Send className="h-3.5 w-3.5" />
            Sent
            {sent.length > 0 && (
              <Badge variant="secondary" className="h-4 min-w-[18px] px-1 text-[9px] ml-0.5">
                {sent.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Inbox */}
        <TabsContent value="inbox" className="mt-4">
          <Card>
            <CardContent className="divide-y divide-border p-0">
              {inboxLoading ? (
                <div className="p-4 space-y-3">
                  {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : inbox.length === 0 ? (
                <EmptyState label="inbox messages" />
              ) : (
                inbox.map((msg) => (
                  <MessageRow
                    key={msg.id}
                    message={msg}
                    isRead={readIds.has(msg.id)}
                    onRead={handleRead}
                    isInbox
                  />
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sent */}
        <TabsContent value="sent" className="mt-4">
          <Card>
            <CardContent className="divide-y divide-border p-0">
              {sentLoading ? (
                <div className="p-4 space-y-3">
                  {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : sent.length === 0 ? (
                <EmptyState label="sent messages" />
              ) : (
                sent.map((msg) => (
                  <MessageRow
                    key={msg.id}
                    message={msg}
                    isRead
                    onRead={() => {}}
                    isInbox={false}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Compose sheet */}
      <ComposeSheet
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        orgId={orgId}
        userRole={user?.role ?? "host"}
      />
    </div>
  );
}
